import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useMemo, useCallback } from 'react';
import useId from '../../utils/use-id.js';
import { Label } from '../label/label.component.js';
import { Text } from '../text/text.component.js';
import { SelectGroup, OptionsGroup, Caret, StyledSelect } from './select.style.js';

const Select = forwardRef((props, ref) => {
    const { className = '', disabled, errorText, helperText, id, label, loading, loadingMessage, margin, minHeight = '110px', name, onChange, options = [], suggestedOptions, required, value, withEmptyPlaceholder, placeholder } = props, rest = __rest(props, ["className", "disabled", "errorText", "helperText", "id", "label", "loading", "loadingMessage", "margin", "minHeight", "name", "onChange", "options", "suggestedOptions", "required", "value", "withEmptyPlaceholder", "placeholder"]);
    const elementId = useId('select', id);
    const optionPlaceholder = useMemo(() => {
        if (withEmptyPlaceholder)
            return (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
            jsx("option", { value: "" }, "placeholder"));
        if (placeholder)
            return (jsx("option", Object.assign({ value: "", disabled: true }, { children: placeholder }), "placeholder"));
    }, [placeholder, withEmptyPlaceholder]);
    const optionLoading = useMemo(() => jsx("option", { children: loadingMessage || 'Loading...' }, "loading"), [loadingMessage]);
    const createOption = useCallback(({ text, value, selected, disabled }) => {
        const optionAttributes = {
            key: `${value || text}`,
            value: value || text,
        };
        if (selected)
            optionAttributes.selected = selected;
        if (disabled)
            optionAttributes.disabled = disabled;
        return jsx("option", Object.assign({}, optionAttributes, { children: text }), void 0);
    }, []);
    const optionsStandard = useMemo(() => options.map(createOption), [options, createOption]);
    const optionsSuggested = useMemo(() => {
        if (!(suggestedOptions === null || suggestedOptions === void 0 ? void 0 : suggestedOptions.length))
            return [];
        return suggestedOptions.map(createOption);
    }, [suggestedOptions, createOption]);
    /* eslint-disable i18next/no-literal-string */
    const optionSeparator = useMemo(() => {
        if (!(suggestedOptions === null || suggestedOptions === void 0 ? void 0 : suggestedOptions.length))
            return;
        return (jsx("option", Object.assign({ disabled: true }, { children: "- - - - - - - - - - - - - - -" }), "separator"));
    }, [suggestedOptions]);
    /* eslint-enable i18next/no-literal-string */
    const getOptions = useMemo(() => {
        if (loading)
            return [optionLoading];
        return [
            optionPlaceholder,
            ...optionsSuggested,
            optionSeparator,
            ...optionsStandard,
        ].filter(Boolean);
    }, [
        loading,
        optionPlaceholder,
        optionsStandard,
        optionsSuggested,
        optionSeparator,
        optionLoading,
    ]);
    const shouldBeDisabled = disabled || loading;
    return (jsxs(SelectGroup, Object.assign({ className: className, margin: margin, minHeight: minHeight }, { children: [jsx(Label, Object.assign({ htmlFor: elementId, disabled: shouldBeDisabled, margin: { bottom: '8px' }, required: required }, { children: label }), void 0), jsxs(OptionsGroup, { children: [jsx(Caret, { disabled: shouldBeDisabled, icon: "ChevronDown" }, void 0), jsx(StyledSelect, Object.assign({ disabled: shouldBeDisabled, error: !!errorText, id: elementId, name: name, onChange: (e) => onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value, e), ref: ref, required: required, value: value }, rest, { children: getOptions }), void 0)] }, void 0), !!helperText && (jsx(Text, Object.assign({ textStyle: "paragraph400", color: "secondary", margin: { top: '8px' } }, { children: helperText }), void 0)), !!errorText && !disabled && (jsx(Text, Object.assign({ textStyle: "paragraph400", color: "error", margin: { top: '8px' } }, { children: errorText }), void 0))] }), void 0));
});

export { Select };
